"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  EXPRESSION_ERROR_STRINGS,
  expressionErrorMessage,
} from "@/components/tools/calculus/expression-errors";
import {
  type Constant,
  readConstant,
} from "@/components/tools/calculus/read-constant";
import { TangentResults } from "@/components/tools/calculus/tangent-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type Node,
  substitute,
  variable as variableNode,
} from "@/lib/tool-kit/calculus/ast";
import { differentiate } from "@/lib/tool-kit/calculus/differentiate";
import { type ExprError, parseExpression } from "@/lib/tool-kit/calculus/parse";
import { type TangentResult, tangentAt } from "@/lib/tool-kit/calculus/tangent";

/** English strings. A translated content file overrides these through `ui`. */
export const TANGENT_LINE_CALCULATOR_STRINGS = {
  functionLabel: "Function f(x)",
  functionHint:
    "Use + − * / ^ and brackets. 2x, x sin x and (x+1)(x−1) multiply. Functions: sin, cos, tan, sec, csc, cot, asin, acos, atan, sinh, cosh, tanh, exp, ln, log (base 10), sqrt, abs. Constants: pi and e.",
  pointLabel: "Point of tangency: x =",
  pointHint: "A number or a constant such as 2, −1.5 or pi/4.",
  estimateLabel: "Estimate f(x) at x = (optional)",
  estimateHint: "Compares the tangent line's value with the true f(x).",
  widthLabel: "Graph half-width around the point",
  equalScale: "Same scale on both axes, so the normal line looks perpendicular",
  results: "Results",
  pointValue: "Point on the curve",
  slopeValue: "Slope of the tangent, f′(a)",
  normalSlopeValue: "Slope of the normal, −1/f′(a)",
  verticalNormal: "vertical (the tangent is horizontal)",
  tangentHeading: "Tangent line",
  normalHeading: "Normal line",
  pointSlopeForm: "Point-slope form",
  slopeInterceptForm: "Slope-intercept form",
  standardForm: "Standard form",
  decimalForm: "As decimals",
  decimalLine: "y ≈ {line}",
  verticalLineNote: "A vertical line: it has no slope.",
  copyTangent: "Copy tangent line",
  copyNormal: "Copy normal line",
  copied: "Copied!",
  approxHeading: "Linear approximation",
  approxIntro:
    "Near x = {point}, the tangent line L(x) = f({point}) + f′({point})(x − {point}) is a good estimate of f(x).",
  approxLinear: "Tangent value L(x)",
  approxActual: "True value f(x)",
  approxError: "Error f(x) − L(x)",
  approxRelative: "Relative error",
  approxWarning:
    "The estimate is far from the true value: x is not close to the point of tangency, or the curve bends sharply there.",
  graphHeading: "Graph",
  graphTitle:
    "Graph of y = f(x), its tangent line and its normal line at x = {point}",
  seriesFunction: "y = f(x)",
  seriesTangent: "Tangent line",
  seriesNormal: "Normal line",
  pointLabelText: "({x}, {y})",
  downloadPng: "Download graph (PNG)",
  downloadSvg: "Download graph (SVG)",
  undefinedHere: "undefined",
  pointInvalid:
    "Enter the point as a number or a constant expression such as 2, −1.5 or pi/4 (no letters other than pi and e).",
  enterPoint: "Enter the x-value of the point to find the tangent line there.",
  oneVariable:
    "Use a function of one variable, such as x^2 sin(x). This tool finds the tangent to y = f(x).",
  failureUndefined:
    "f is not defined (it has no real value) at x = {point}, so the curve has no point there and no tangent line.",
  failureCorner:
    "The curve has a corner at x = {point}: the slope from the left is about {left} but from the right about {right}, so there is no single tangent line.",
  failureVertical:
    "The tangent at x = {point} is vertical (or the curve only exists on one side there): the slope grows without bound, so f′ does not exist. The tangent line is x = {point}.",
  failureCusp:
    "The curve has a cusp at x = {point}: the slope runs off to +∞ on one side and −∞ on the other, so there is no tangent line.",
  failureUnbounded:
    "f′ does not exist at x = {point}, so there is no tangent line there.",
  expressionErrors: EXPRESSION_ERROR_STRINGS,
} as const;

export type TangentStrings = typeof TANGENT_LINE_CALCULATOR_STRINGS;

export type TangentView =
  | { kind: "parse-error"; error: ExprError }
  | { kind: "many-variables" }
  | { kind: "blank-point" }
  | { kind: "bad-point" }
  | {
      kind: "ok";
      ast: Node;
      derivative: Node;
      point: Constant;
      decimals: boolean;
      result: TangentResult;
    };

function buildView(text: string, pointText: string): TangentView {
  const parsed = parseExpression(text);
  if (!parsed.ok) {
    return { kind: "parse-error", error: parsed.error };
  }
  if (parsed.variables.length > 1) {
    return { kind: "many-variables" };
  }
  const original = parsed.variables[0] ?? "x";
  const ast =
    original === "x"
      ? parsed.ast
      : substitute(parsed.ast, original, variableNode("x"));
  const point = readConstant(pointText);
  if (point === null) {
    return { kind: "blank-point" };
  }
  if (point === "invalid") {
    return { kind: "bad-point" };
  }
  const derivative = differentiate(ast, "x").derivative;
  return {
    kind: "ok",
    ast,
    derivative,
    point,
    decimals: parsed.usesDecimals,
    result: tangentAt(ast, derivative, point.ast, point.value),
  };
}

function problemMessage(
  view: TangentView,
  strings: TangentStrings
): string | null {
  switch (view.kind) {
    case "parse-error":
      return expressionErrorMessage(view.error, strings.expressionErrors);
    case "many-variables":
      return strings.oneVariable;
    case "blank-point":
      return strings.enterPoint;
    case "bad-point":
      return strings.pointInvalid;
    default:
      return null;
  }
}

export default function TangentLineCalculatorWidget() {
  const strings = useToolUi(TANGENT_LINE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("x^3 - 2x");
  const [pointText, setPointText] = useState("1");
  const [estimateText, setEstimateText] = useState("1.1");
  const [widthText, setWidthText] = useState("3");
  const [equal, setEqual] = useState(true);
  const built = useMemo(() => buildView(text, pointText), [text, pointText]);
  const halfWidth = Math.max(parseNumberInput(widthText) ?? 3, 1e-6);
  const message = problemMessage(built, strings);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextField
            hint={strings.functionHint}
            id="tl-function"
            label={strings.functionLabel}
            mono
            onChange={setText}
            value={text}
          />
        </div>
        <TextField
          hint={strings.pointHint}
          id="tl-point"
          label={strings.pointLabel}
          mono
          onChange={setPointText}
          value={pointText}
        />
        <TextField
          hint={strings.estimateHint}
          id="tl-estimate"
          label={strings.estimateLabel}
          mono
          onChange={setEstimateText}
          value={estimateText}
        />
        <TextField
          id="tl-width"
          label={strings.widthLabel}
          mono
          numeric
          onChange={setWidthText}
          value={widthText}
        />
        <div className="self-end">
          <Checkbox
            checked={equal}
            label={strings.equalScale}
            onChange={setEqual}
          />
        </div>
      </div>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {message ? <ErrorNotice>{message}</ErrorNotice> : null}
        {built.kind === "ok" ? (
          <TangentResults
            equalScale={equal}
            estimateText={estimateText}
            fmt={fmt}
            halfWidth={halfWidth}
            strings={strings}
            view={built}
          />
        ) : null}
      </section>
    </>
  );
}
